import { NewsInterface } from "@/types/news";
import { FormatUtils } from "@/utils/format";
import { ArrowRight } from "lucide-react";
import Link from "next/link";

export const NewsCard: React.FC<{ news: NewsInterface }> = ({ news }) => {
  // Function to truncate description with character limit
  const truncateDescription = (text: string, maxLength: number = 120): string => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength).replace(/\s+\S*$/, '') + '...';
  };

  return (
    <Link 
      href={`/tin-tuc/${news.slug}`} 
      aria-label={news.title}
      className="block group w-full"
      prefetch={true}
    >
      <div className="relative w-full h-96 md:h-[200px] lg:h-[300px] xl:h-[400px] overflow-hidden rounded-xl lg:rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:scale-[1.02]">
        {/* Background Image */}
        <img 
          src={news.imageUrl} 
          alt={news.title} 
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 scale-110"
        />
        
        {/* Shimmer Effect */}
        <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent transform -skew-x-12 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000" />
        </div>
        
        {/* Black Blur Overlay with Soft Feather Effect */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent backdrop-blur-[1px]" />
        
        {/* Additional subtle overlay for better text readability */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
        
        {/* Date Badge */}
        <div className="absolute top-4 left-4 md:top-6 md:left-6">
          <div className="bg-black/50 backdrop-blur-sm text-white px-3 py-1.5 rounded-full text-xs md:text-sm font-medium border border-white/20">
            {FormatUtils.formatDate(news.createdAt)}
          </div>
        </div>
        
        {/* Content Overlay */}
        <div className="absolute inset-0 flex flex-col justify-end p-6 md:p-8 lg:p-10">
          {/* Title */}
          <h2 className="text-base md:text-xl lg:text-2xl xl:text-3xl font-bold text-white mb-3 md:mb-4 leading-tight drop-shadow-lg">
            {news.title}
          </h2>
          
          {/* Short Description */}
          <p className="text-sm md:text-base lg:text-lg text-white/90 mb-4 md:mb-6 leading-relaxed font-light drop-shadow-md max-w-4xl">
            {truncateDescription(news.shortDescription ??"")}
          </p>
          
          {/* Read More Button */}
          {/* <div className="inline-flex items-center text-white hover:text-amber-300 font-semibold text-base md:text-lg transition-colors duration-300 group-hover:translate-x-2 transform transition-transform">
            <span>Xem bài viết</span>
            <ArrowRight className="ml-2 w-5 h-5 md:w-6 md:h-6 group-hover:translate-x-1 transition-transform duration-300" />
          </div> */}
        </div>
        
        {/* Hover Border Effect */}
        <div className="absolute inset-0 border-2 border-transparent group-hover:border-amber-400/50 rounded-xl lg:rounded-2xl transition-all duration-300 pointer-events-none" />
      </div>
    </Link>
  );
};